import { useState, useEffect } from "react"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { SubmitHandler, useForm } from "react-hook-form"
import { faLocationPin, faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { FormLabel, Modal, Pagination, TextInput, Button as WFPButton, Search, Blockquote } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import OverlayLoader from "components/Shared/OverlayLoader/OverlayLoader"
import { GET_ITEM_LIMIT, MARKET_MODULE, MFI_MODULE, RPME_MODULE } from "constants/constants"
import { Market, SimpleSelect } from "interfaces/dataInterfaces"
import {
  useDeleteMarketMutation,
  useGetMarketsQuery,
  useUpdateMarketMutation,
  useGetNearbyMarketsMutation,
  useGetListOfNearbyMarketsMutation,
  useAddMarketMutation,
} from "services/core/markets"
import { useLocatePointMutation } from "services/core/geoBoundaries"
import Map from "components/Shared/Leaflet/Map"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import DownloadMarketButtons from "components/Shared/Common/DownloadMarketButtons"

type Inputs = {
  marketName: string
  marketNameC: string // for custom error message
  marketLocalName: string
  marketLatitude: any
  marketLongitude: any
}

const permissionsProps = {
  moduleId: [MARKET_MODULE, RPME_MODULE, MFI_MODULE],
  subModule: "markets",
}

export default function MarketsPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setError,
    clearErrors,
  } = useForm<Inputs>()

  const [country, setCountry] = useState<undefined | SimpleSelect>()
  const [coordinates, setCoordinates] = useState<[number, number] | undefined>()
  const [actionItem, setActionItem] = useState<{
    item: Market
    action: "edit" | "delete"
  } | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)
  const [inputText, setInputText] = useState<string>("")
  const [query, setQuery] = useState("")
  const [showNewMarketModal, setShowNewMarketModal] = useState(false)
  const { data: markets, isFetching: fetchingMarkets } = useGetMarketsQuery(
    {
      adm0code: country?.value ?? 0,
      skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
      top: getItemLimit,
      query,
    },
    { skip: !country },
  )
  const [deleteMarket] = useDeleteMarketMutation()
  const [updateMarket] = useUpdateMarketMutation()
  const [addMarket] = useAddMarketMutation()
  const [locatePoint, { data: locateData, reset: locateDataReset }] = useLocatePointMutation()
  const [nearbyMarkets, { data: nearbyMarketsData, reset: nearbyMarketsReset }] = useGetNearbyMarketsMutation()
  const [geoJSONList, { data: geoList }] = useGetListOfNearbyMarketsMutation()

  const onSubmit: SubmitHandler<Inputs> = async data => {
    if (actionItem?.action === "edit") {
      await updateMarket({
        marketID: actionItem.item.marketID,
        marketName: data.marketName?.trim(),
        marketLocalName: data.marketLocalName?.trim(),
        marketLatitude: Number(data.marketLatitude?.trim()) || 0,
        marketLongitude: Number(data.marketLongitude?.trim()) || 0,
        adm0Code: country?.value as number,
        adm1Code: actionItem.item.adm1Code,
        adm2Code: actionItem.item.adm2Code,
      })
      setActionItem(null)
    } else {
      await addMarket({
        ...data,
        marketName: data.marketName?.trim(),
        marketLocalName: data.marketLocalName?.trim(),
        marketLatitude: Number(data.marketLatitude?.trim()) || 0,
        marketLongitude: Number(data.marketLongitude?.trim()) || 0,
        adm0Code: country?.value as number,
        adm1Code: locateData?.adm1Code,
        adm2Code: locateData?.adm2Code,
      })
      setShowNewMarketModal(false)
    }
    nearbyMarketsReset()
    locateDataReset()
    reset()
  }

  const columns: Array<ColumnShape<Market>> = [
    {
      key: "marketID",
      dataKey: "marketID",
      title: "ID",
      width: 80,
      maxWidth: 80,
    },
    {
      key: "adm1Name",
      dataKey: "adm1Name",
      title: "Adm-1",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "adm2Name",
      dataKey: "adm2Name",
      title: "Adm-2",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "marketName",
      dataKey: "marketName",
      title: "Name",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "marketLocalName",
      dataKey: "marketLocalName",
      title: "Local Name",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "marketLatitude",
      dataKey: "marketLatitude",
      title: "Latitude",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "marketLongitude",
      dataKey: "marketLongitude",
      title: "Longitude",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "action",
      align: Column.Alignment.CENTER,
      width: 300,
      cellRenderer: ({ rowData }) => (
        <>
          <PermissionWrapper {...permissionsProps} feature="edit">
            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "edit",
                })
              }
              label="Edit"
              className="btn-full blue action-btn"
              icon={faPen}
            />
          </PermissionWrapper>
          <PermissionWrapper {...permissionsProps} feature="delete">
            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "delete",
                })
              }
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "10px" }}
              icon={faTrash}
            />
          </PermissionWrapper>
        </>
      ),
    },
  ]

  const handleMarketValidation = () => {
    const val = getValues("marketName")
    if (val?.toLowerCase() === actionItem?.item?.marketName?.toLowerCase()) return
    const nearbyMarketFeatures = geoList?.features ?? []
    const exist = nearbyMarketFeatures.some(feature => feature.properties?.name?.toLowerCase() === val.toLowerCase())
    if (exist) setError("marketNameC", { type: "duplicate", message: "Market exist with this name." })
    else clearErrors("marketNameC")
  }

  const handleDelete = async (id: number) => {
    await deleteMarket(id)
    setActionItem(null)
  }

  const handleSearch = (): void => {
    setPageNumber(1)
    setQuery(inputText)
  }

  const handleNewEditModalClose = () => {
    if (actionItem) {
      setActionItem(null)
    } else {
      setShowNewMarketModal(false)
    }
    nearbyMarketsReset()
    locateDataReset()
    reset()
  }

  const handleLocalize = async () => {
    const lat = getValues("marketLatitude")
    const long = getValues("marketLongitude")
    if (!lat || !long) return
    locatePoint({ lat, long, adm0Code: country?.value as number }).unwrap()
  }

  const handleCountry = (item?: SimpleSelect): void => {
    setPageNumber(1)
    setInputText("")
    setQuery("")
    setCountry(item)
  }

  useEffect(() => {
    if (locateData && !locateData?.error) {
      const lat = getValues("marketLatitude")
      const long = getValues("marketLongitude")
      nearbyMarkets({ lat, long, adm0Code: country?.value as number })
      setCoordinates([lat, long])
    }
  }, [locateData])

  useEffect(() => {
    if (!actionItem) {
      setCoordinates(undefined)
      return
    }
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { marketLatitude: lat, marketLongitude: long } = actionItem?.item
    nearbyMarkets({ lat, long, adm0Code: country?.value as number })
    setCoordinates([lat, long])
  }, [actionItem])

  useEffect(() => {
    if (!inputText && query) setQuery("")
  }, [inputText])

  return (
    <>
      <HeaderFilters>
        <div className="flex w-3/4 items-center">
          <CountrySelector onSelect={handleCountry} allowAll={false} isClearable={false} />
          <Search
            id="search"
            onChange={e => setInputText(e.toString())}
            value={inputText}
            className="mr-1"
            style={{ borderRadius: 5 }}
            name="input-name"
            // @ts-ignore
            closeButtonLabelText="Clear search input"
            disabled={!country}
            onKeyUp={e => e?.code === "Enter" && handleSearch()}
          />
          <WFPButton onClick={handleSearch} disabled={!country}>
            Search
          </WFPButton>
        </div>

        <PermissionWrapper {...permissionsProps} feature="addNew">
          <Button
            action={() => {
              if (!country || !country?.value || country.value === -1) {
                alert("Please select a country first!")
                return
              }
              setShowNewMarketModal(true)
              geoJSONList({ adm0Code: country?.value })
            }}
            label="Add New Market"
            className="btn-full green"
            icon={faPlus}
          />
        </PermissionWrapper>
      </HeaderFilters>

      <DownloadMarketButtons adm0Code={country?.value} hide={["commodityList"]} />

      {country ? (
        <>
          <div className="relative">
            <div className="h-full min-h-[50vh] mt-3">
              <AutoResizer>
                {({ width, height }) => (
                  <BaseTable
                    data={markets?.items}
                    width={width}
                    height={height}
                    rowKey="marketID"
                    className="custom-table"
                    rowHeight={38}
                    headerHeight={45}
                    columns={columns}
                    emptyRenderer={() => <p>No records to display</p>}
                  />
                )}
              </AutoResizer>
            </div>
            <OverlayLoader active={fetchingMarkets} />
          </div>
          <Pagination
            totalItems={markets?.count ?? 0}
            pageSize={getItemLimit}
            pageSizes={[10, 20, 30]}
            page={pageNumber}
            onChange={event => {
              setPageNumber((event as any).page)
              setGetItemLimit((event as any).pageSize)
            }}
          />
        </>
      ) : (
        <Blockquote withIcon style={{ height: 100 }} className="my-8">
          <p className="mt-2">Please select a country to view markets.</p>
        </Blockquote>
      )}

      <Modal
        open={actionItem?.action === "delete"}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(actionItem!.item.marketID)}
        onRequestClose={() => setActionItem(null)}
      >
        Are you sure you want to delete the selected record?
      </Modal>

      {(showNewMarketModal || actionItem?.action === "edit") && (
        <Modal
          open={showNewMarketModal || actionItem?.action === "edit"}
          modalHeading={actionItem?.action === "edit" ? "Edit Market" : "Add New Market"}
          primaryButtonText="Save"
          primaryButtonDisabled={!!Object.keys(errors).length || !!locateData?.error || !locateData}
          secondaryButtonText="Cancel"
          onRequestSubmit={handleSubmit(onSubmit)}
          onRequestClose={handleNewEditModalClose}
          wide
        >
          <form>
            <div className="grid grid-cols-5 justify-between gap-x-10 px-6">
              <div className="w-full col-span-3 space-y3">
                <FormLabel className="d-flex items-center">
                  <span className="w-24">Country</span>
                  <TextInput
                    className="ml-3 bg-gray-300"
                    placeholder="Country"
                    defaultValue={country?.label}
                    disabled
                  />
                </FormLabel>
                <FormLabel className="d-flex items-center">
                  <span className="w-24"> Name</span>
                  <TextInput
                    className="ml-3"
                    placeholder="Name"
                    id="marketName"
                    defaultValue={actionItem?.item.marketName}
                    invalid={!!errors.marketNameC?.message}
                    invalidText={errors.marketNameC?.message || ""}
                    {...register("marketName", { required: "Market Name is required" })}
                    onBlur={handleMarketValidation}
                    onMouseLeave={handleMarketValidation}
                    onMouseEnter={handleMarketValidation}
                  />{" "}
                </FormLabel>
                <FormLabel className="d-flex items-center">
                  <span className="w-24">Local Name</span>
                  <TextInput
                    className="ml-3"
                    placeholder="Local Name"
                    defaultValue={actionItem?.item.marketLocalName ?? ""}
                    {...register("marketLocalName")}
                  />{" "}
                </FormLabel>
              </div>

              <div className="w-full space-y-3 col-span-2">
                <div className="flex items-end gap-x-7 w-full">
                  <TextInput
                    labelText="Latitude"
                    placeholder="Latitude"
                    type="decimal"
                    defaultValue={actionItem?.item.marketLatitude}
                    {...register("marketLatitude", { required: "Latitude is required" })}
                  />
                  <TextInput
                    labelText="Longitude"
                    placeholder="Longitude"
                    type="decimal"
                    defaultValue={actionItem?.item.marketLongitude}
                    {...register("marketLongitude", { required: "Longitude is required" })}
                  />
                  <Button action={handleLocalize} label="Localize" className="btn-full green" icon={faLocationPin} />
                </div>
                <div>
                  <p>
                    Administrative area:{" "}
                    {locateData && !locateData?.error && `${locateData?.adm1Name} - ${locateData?.adm2Name}`}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-2 grid grid-cols-6">
              <div className="col-span-4">
                <Map adm0Code={country?.value as number} coordinates={coordinates} />
              </div>
              <div className="col-span-2 ml-4">
                {locateData?.error && <p>{locateData.error}</p>}
                {locateData && !locateData.error && (
                  <div>
                    <p className="mb-1">
                      The coordinates that has been provided are located inside the administrative unit &ldquo;
                      {`${locateData?.adm1Name} - ${locateData?.adm2Name}`}&ldquo;.
                    </p>
                    <p className="mb-2">
                      Please review on the map if the location is correct and it&lsquo;s not overlapping other existing
                      markets.
                    </p>
                    {nearbyMarketsData?.length === 0 && <p>There are no other registered markets within 15Km.</p>}
                    {!!nearbyMarketsData?.length && (
                      <div id="nearbyMarketsInfo">
                        <p>List of nearest markets:</p>
                        <ul className="my-1">
                          {nearbyMarketsData?.map(market => (
                            <li key={market.marketId} className="flex mb-1 items-baseline gap-x-2">
                              <div className={`w-3 h-3 ${market.distance <= 500 ? "bg-red-500" : "bg-yellow-300"}`} />
                              <div>{market.marketName}</div>
                              <div className="text-sm text-gray-500">
                                {market.distance < 100
                                  ? `${(market.distance || 0).toFixed(0)} m`
                                  : `${((market.distance || 0) / 1000).toFixed(1)} km`}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </Modal>
      )}
    </>
  )
}
